/** Global inspector wallet controls never treat a Checkout redirect as payment proof. */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../shared/ui/select';
import { createSITopup, loadSITopupOptions, verifySITopup, type SITopupOptions } from './siTopups';

const STORAGE_KEY = 'quantis:si-pending-topup';
interface PendingTopup { workspaceId: string; topupId: string }
function storedTopup(workspaceId: string): PendingTopup | null {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
    if (value && typeof value === 'object' && 'workspaceId' in value && value.workspaceId === workspaceId
      && 'topupId' in value && typeof value.topupId === 'string' && /^[0-9a-f-]{36}$/i.test(value.topupId))
      return { workspaceId, topupId: value.topupId };
  } catch { /* Unavailable storage cannot establish a pending checkout. */ }
  return null;
}
function clearReturnParameter(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.has('si_topup')) {
    url.searchParams.delete('si_topup');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
}
const gbp = (pence: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(pence / 100);
export function SITopupControls({ workspaceId, onBalanceUpdated, hideUnavailable = false, onCheckoutNavigate = url => window.location.assign(url) }:
  { workspaceId?: string | null; onBalanceUpdated?: () => void; hideUnavailable?: boolean; onCheckoutNavigate?: (url: string) => void }) {
  const [options, setOptions] = useState<SITopupOptions | null>(null);
  const [amount, setAmount] = useState(''); const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null); const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingTopup | null>(null);
  const controller = useRef<AbortController | null>(null);
  const refreshed = useRef(onBalanceUpdated); refreshed.current = onBalanceUpdated;
  useEffect(() => {
    const abort = new AbortController(); controller.current?.abort(); controller.current = abort;
    setOptions(null); setError(null); setNotice(null); setBusy(false); setAmount(''); setPending(null);
    if (!workspaceId) return () => abort.abort();
    void loadSITopupOptions(workspaceId, abort.signal).then(value => {
      if (abort.signal.aborted) return;
      setOptions(value); setAmount(String(value.packsPence[0] ?? ''));
    }).catch(cause => { if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : 'QuantisAI billing is unavailable.'); });
    const saved = storedTopup(workspaceId); setPending(saved);
    const returned = new URL(window.location.href).searchParams.get('si_topup');
    if (returned === 'return' && saved) {
      setBusy(true);
      void verifySITopup(workspaceId, saved.topupId, abort.signal).then(result => {
        if (abort.signal.aborted) return;
        if (result.credited) {
          sessionStorage.removeItem(STORAGE_KEY); setPending(null); refreshed.current?.();
          setNotice('Payment verified. QuantisAI balance updated.'); clearReturnParameter();
        } else setNotice(result.paid ? 'Payment received. Balance is awaiting payment synchronisation. Check payment again shortly.'
          : 'Payment has not been confirmed. No balance has been added.');
      }).catch(cause => { if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : 'Payment verification failed.'); })
        .finally(() => { if (!abort.signal.aborted) setBusy(false); });
    } else if (returned === 'cancelled' && saved) {
      setNotice('Checkout cancelled. No payment has been confirmed.'); clearReturnParameter();
    }
    return () => { abort.abort(); controller.current?.abort(); };
  }, [workspaceId]);
  const buy = async () => {
    if (!workspaceId || !options?.enabled || busy || !options.packsPence.includes(Number(amount))) return;
    const abort = new AbortController(); controller.current?.abort(); controller.current = abort;
    setBusy(true); setError(null); setNotice(null);
    try {
      const checkout = await createSITopup(workspaceId, Number(amount), abort.signal);
      if (abort.signal.aborted) return;
      if (options.environment && options.environment !== checkout.environment) throw new Error('Payment environment changed. Reopen QuantisAI before retrying.');
      const saved = { workspaceId, topupId: checkout.topupId };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(saved)); setPending(saved);
      onCheckoutNavigate(checkout.url);
    } catch (cause) { if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : 'Checkout could not be opened.'); }
    finally { if (!abort.signal.aborted) setBusy(false); }
  };
  const verify = async () => {
    if (!workspaceId || !pending || busy) return;
    const abort = new AbortController(); controller.current?.abort(); controller.current = abort;
    setBusy(true); setError(null);
    try {
      const result = await verifySITopup(workspaceId, pending.topupId, abort.signal);
      if (abort.signal.aborted) return;
      if (result.credited) {
        sessionStorage.removeItem(STORAGE_KEY); setPending(null); refreshed.current?.();
        setNotice('Payment verified. QuantisAI balance updated.'); clearReturnParameter();
      } else setNotice(result.paid ? 'Payment received. Balance is awaiting payment synchronisation. Check payment again shortly.'
        : 'Payment has not been confirmed. No balance has been added.');
    } catch (cause) { if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : 'Payment verification failed.'); }
    finally { if (!abort.signal.aborted) setBusy(false); }
  };
  if (!workspaceId || (hideUnavailable && !options?.enabled && !pending && !error)) return null;
  return <section aria-label="Add QuantisAI balance" className="border-t px-4 py-3 text-xs">
    <p className="font-medium">QuantisAI balance</p>
    {options === null && !error && <p role="status" className="mt-1 text-muted-foreground">Checking topup availability...</p>}
    {options && !options.enabled && <p role="status" className="mt-1 text-muted-foreground">{options.disabledReason}</p>}
    {options?.enabled && <>
      <p className="mt-1 text-muted-foreground">Add balance for QuantisAI usage. Project credits are separate.</p>
      {options.environment === 'test' && <p className="mt-1 text-muted-foreground">Test payment environment. This balance cannot fund live usage.</p>}
      <div className="mt-2 flex items-center gap-2">
        <Select value={amount} onValueChange={setAmount} disabled={busy}>
          <SelectTrigger className="h-8 flex-1" aria-label="QuantisAI topup amount"><SelectValue /></SelectTrigger>
          <SelectContent>{options.packsPence.map(pack => <SelectItem key={pack} value={String(pack)}>{gbp(pack)}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="button" size="sm" disabled={busy || !amount} onClick={() => void buy()}>{busy ? 'Please wait...' : 'Add balance'}</Button>
      </div>
    </>}
    {pending && options?.canManage && <Button type="button" size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => void verify()}>Check payment</Button>}
    {notice && <p role="status" className="mt-2 text-muted-foreground">{notice}</p>}
    {error && <p role="alert" className="mt-2 text-destructive">{error}</p>}
  </section>;
}
